import type { ReactNode } from 'react';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router';

import {
  buildLanguagePath,
  detectLanguage,
  isSupportedLanguage,
} from '../../lib/language';

import FlotiqFlowbiteConfigProvider from '@flotiq/flotiq-flowbite/dist/contexts/FlotiqFlowbiteConfigContext/FlotiqFlowbiteConfigContextProvider';

import LoaderPage from '../../pages/LoaderPage/LoaderPage';

export interface LanguageWrapperProps {
  children?: ReactNode;
}

export const LanguageWrapper = ({ children }: LanguageWrapperProps) => {
  const currentChildren = children ? children : <Outlet />;
  const [isLoading, setIsLoading] = useState(true);

  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!lang || i18n.language === lang) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    i18n.changeLanguage(lang).finally(() => setIsLoading(false));
  }, [i18n, lang]);

  if (!lang) {
    return (
      <Navigate
        to={buildLanguagePath(
          detectLanguage(),
          window.location.pathname,
          window.location.search,
        )}
        replace
      />
    );
  }

  if (!isSupportedLanguage(lang)) {
    return <Navigate to="/" replace />;
  }

  return (
    <FlotiqFlowbiteConfigProvider
      value={{
        hrefPrefix: `/${lang}`,
        lang,
      }}
    >
      {isLoading ? <LoaderPage /> : currentChildren}
    </FlotiqFlowbiteConfigProvider>
  );
};

export default LanguageWrapper;
